import type { EsteiraFila, EsteiraGrupo } from '../../../types';

/**
 * Fonte única da esteira na interface — mesmo papel que `etapas.ts` cumpre para
 * a régua de cobrança. Se uma fila mudar de nome, de cor ou de instrução, muda
 * aqui: a tela, os filtros, os KPIs e os textos de ajuda leem tudo deste arquivo.
 */

// Os formatadores e o cálculo de dias já existem na régua — reaproveitados em
// vez de duplicados, senão as duas telas divergiriam no fuso.
export { formatBRL, formatDia, diasDesde } from '../LembretesCobranca/etapas.ts';

export interface GrupoMeta {
    grupo: EsteiraGrupo;
    titulo: string;
    descricao: string;
    accent: string;
}

export const GRUPOS: GrupoMeta[] = [
    {
        grupo: 'regua',
        titulo: 'Régua de cobrança',
        descricao: 'Contato humano D+4 e D+10, conferência D+24',
        accent: 'border-l-amber-400',
    },
    {
        grupo: 'notificacoes',
        titulo: 'Notificações extrajudiciais',
        descricao: 'Do D+17 ao fim do prazo: gerar, assinar, enviar e conferir',
        accent: 'border-l-orange-700',
    },
    {
        grupo: 'documentos',
        titulo: 'Documentos',
        descricao: 'Advertência e distrato',
        accent: 'border-l-orange-500',
    },
    {
        grupo: 'negativacao',
        titulo: 'Negativação',
        descricao: 'Pôr no SPC, tirar quando regulariza e quem está negativado',
        accent: 'border-l-red-500',
    },
    {
        grupo: 'negociacao',
        titulo: 'Negociação',
        descricao: 'Retornos agendados, débitos sem contato e cobranças sem dono',
        accent: 'border-l-purple-500',
    },
];

export interface FilaMeta {
    fila: EsteiraFila;
    grupo: EsteiraGrupo;
    titulo: string;
    /** O que a pessoa tem que fazer — vira o texto de ajuda do card. */
    instrucao: string;
    /**
     * Rótulo do botão principal **da régua** (D+10/17/24), que é a única fila com
     * uma ação própria registrável.
     *
     * ⚠️ Não reaproveite isto em botão de outra fila. Já foi usado no botão que
     * abre o painel de negociação e o rótulo mentia: numa fila de documento lia
     * "Abrir documento" e abria a negociação. Nas filas derivadas a ação está em
     * "Assumir", "Anotar andamento", "Abrir PDF" e "Abrir na UC"; o que a pessoa
     * precisa fazer está em `instrucao`.
     */
    acaoLabel: string;
    badge: string;
    accent: string;
}

export const FILAS: Record<EsteiraFila, FilaMeta> = {
    regua_d4: {
        fila: 'regua_d4',
        grupo: 'regua',
        titulo: 'D+4 · Primeiro contato humano',
        instrucao: 'Ligue ou mande mensagem lembrando do boleto em atraso e oferecendo a segunda via.',
        acaoLabel: 'Registrar contato',
        badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        accent: 'border-l-yellow-400',
    },
    regua_d10: {
        fila: 'regua_d10',
        grupo: 'regua',
        titulo: 'D+10 · Contato humano',
        instrucao: 'Ligue ou mande mensagem informando o atraso e o risco de negativação.',
        acaoLabel: 'Registrar contato',
        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        accent: 'border-l-amber-400',
    },
    regua_d17: {
        fila: 'regua_d17',
        grupo: 'notificacoes',
        titulo: 'D+17 · Notificação extrajudicial',
        instrucao: 'Gere a notificação e registre o envio ao cliente — é o envio que inicia o prazo de 5 dias úteis.',
        acaoLabel: 'Gerar notificação',
        badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
        accent: 'border-l-orange-500',
    },
    notificacao_extrajudicial: {
        fila: 'notificacao_extrajudicial',
        grupo: 'notificacoes',
        titulo: 'Notificação extrajudicial · encaminhada',
        instrucao: 'O cliente foi encaminhado para a notificação extrajudicial no "Gerenciar Cobrança". Gere a notificação e registre o envio — é o envio que inicia o prazo de 5 dias úteis.',
        acaoLabel: 'Gerar notificação',
        badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
        accent: 'border-l-orange-500',
    },
    regua_d24: {
        fila: 'regua_d24',
        grupo: 'regua',
        titulo: 'D+24 · Conferência e negativação',
        instrucao: 'Confira se o pagamento entrou. Se não entrou, negative o consumidor no SPC.',
        acaoLabel: 'Registrar conferência',
        badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        accent: 'border-l-red-500',
    },

    mora_sem_assinatura: {
        fila: 'mora_sem_assinatura',
        grupo: 'notificacoes',
        titulo: 'Mora · aguardando assinatura',
        instrucao: 'O procurador ainda não assinou. Cobre a assinatura ou cancele o documento se ele perdeu o sentido.',
        acaoLabel: 'Abrir documento',
        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        accent: 'border-l-amber-400',
    },
    mora_sem_envio: {
        fila: 'mora_sem_envio',
        grupo: 'notificacoes',
        titulo: 'Mora · assinada, falta enviar',
        instrucao: 'Baixe o PDF assinado, envie ao cliente e registre o envio — sem isso o prazo de 5 DU não começa.',
        acaoLabel: 'Registrar envio',
        badge: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
        accent: 'border-l-teal-500',
    },
    mora_prazo_vencido: {
        fila: 'mora_prazo_vencido',
        grupo: 'notificacoes',
        titulo: 'Mora · prazo encerrado',
        instrucao: 'Os 5 dias úteis acabaram. Verifique se o pagamento entrou e registre o desfecho.',
        acaoLabel: 'Registrar desfecho',
        badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
        accent: 'border-l-rose-500',
    },

    advertencia_sem_assinatura: {
        fila: 'advertencia_sem_assinatura',
        grupo: 'documentos',
        titulo: 'Advertência · aguardando assinatura',
        instrucao: 'O procurador ainda não assinou o comunicado de advertência.',
        acaoLabel: 'Abrir documento',
        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        accent: 'border-l-amber-400',
    },
    advertencia_sem_envio: {
        fila: 'advertencia_sem_envio',
        grupo: 'documentos',
        titulo: 'Advertência · assinada, falta enviar',
        instrucao: 'Baixe o PDF assinado, envie ao cliente e registre o envio.',
        acaoLabel: 'Registrar envio',
        badge: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
        accent: 'border-l-teal-500',
    },
    advertencia_prazo_vencido: {
        fila: 'advertencia_prazo_vencido',
        grupo: 'documentos',
        titulo: 'Advertência · prazo encerrado',
        instrucao: 'O prazo de 15 dias acabou. Verifique a regularização e registre o desfecho.',
        acaoLabel: 'Registrar desfecho',
        badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
        accent: 'border-l-rose-500',
    },

    distrato_em_aberto: {
        fila: 'distrato_em_aberto',
        grupo: 'documentos',
        titulo: 'Distrato · a conferir',
        // Estado de transição: termo gerado que a reconciliação ainda não olhou.
        instrucao: 'Termo gerado e ainda não conferido no Autentique. A reconciliação preenche o estado no próximo ciclo.',
        acaoLabel: 'Ver histórico',
        badge: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
        accent: 'border-l-slate-400',
    },
    distrato_aguardando_vigor: {
        fila: 'distrato_aguardando_vigor',
        grupo: 'documentos',
        titulo: 'Distrato · aguardando a Vigor',
        // A fila mais acionável das três: depende de gente de casa.
        instrucao: 'Falta assinatura de alguém da Vigor. Não depende do cliente — cobre internamente e destrava hoje.',
        acaoLabel: 'Ver histórico',
        badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
        accent: 'border-l-orange-500',
    },
    distrato_aguardando_cliente: {
        fila: 'distrato_aguardando_cliente',
        grupo: 'documentos',
        titulo: 'Distrato · aguardando o cliente',
        instrucao: 'A Vigor já assinou. Falta o cliente — cobre a assinatura por WhatsApp ou e-mail.',
        acaoLabel: 'Ver histórico',
        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        accent: 'border-l-amber-400',
    },
    distrato_assinado_sem_envio: {
        fila: 'distrato_assinado_sem_envio',
        grupo: 'documentos',
        titulo: 'Distrato · assinado, falta enviar',
        instrucao: 'Todas as assinaturas saíram. Baixe o termo, envie ao cliente e registre o envio para encerrar.',
        acaoLabel: 'Registrar envio',
        badge: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
        accent: 'border-l-teal-500',
    },

    negativacao_pendente: {
        fila: 'negativacao_pendente',
        grupo: 'negativacao',
        titulo: 'Negativação pendente',
        instrucao: 'Foi marcado para negativar mas ninguém efetivou no SPC. Efetive e registre.',
        acaoLabel: 'Gerenciar negociação',
        badge: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        accent: 'border-l-red-500',
    },
    negativacao_retirar: {
        fila: 'negativacao_retirar',
        grupo: 'negativacao',
        titulo: 'Retirar negativação',
        instrucao: 'O cliente negativado pagou ou fechou acordo. Retire a negativação no SPC e registre a retirada.',
        acaoLabel: 'Registrar retirada',
        badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
        accent: 'border-l-emerald-500',
    },
    negativado_spc: {
        fila: 'negativado_spc',
        grupo: 'negativacao',
        titulo: 'Negativado no SPC',
        instrucao: 'Está negativado e segue devendo. Quando pagar ou fechar acordo, ele passa sozinho para "Retirar negativação".',
        acaoLabel: 'Gerenciar negociação',
        badge: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
        accent: 'border-l-gray-400',
    },
    followup_vencido: {
        fila: 'followup_vencido',
        grupo: 'negociacao',
        titulo: 'Retorno agendado',
        instrucao: 'Chegou o dia combinado para voltar a falar com o cliente. Registre o contato e, se precisar, agende o próximo.',
        acaoLabel: 'Gerenciar negociação',
        badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
        accent: 'border-l-purple-500',
    },
    sem_retorno: {
        fila: 'sem_retorno',
        grupo: 'negociacao',
        titulo: 'Sem próximo contato',
        instrucao: 'Teve contato, mas ninguém marcou quando voltar a falar com o cliente. Registre o contato com a data do retorno ou com o status.',
        acaoLabel: 'Registrar contato',
        badge: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-300',
        accent: 'border-l-fuchsia-500',
    },
    sem_contato: {
        fila: 'sem_contato',
        grupo: 'negociacao',
        titulo: 'Débito sem contato',
        instrucao: 'Tem fatura vencida e ninguém falou com o cliente por esta dívida. Ligue ou mande mensagem e registre como ficou.',
        acaoLabel: 'Registrar contato',
        badge: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
        accent: 'border-l-sky-500',
    },
    sem_responsavel: {
        fila: 'sem_responsavel',
        grupo: 'negociacao',
        titulo: 'Sem responsável',
        instrucao: 'Cobrança em aberto que ninguém assumiu. Assuma ou atribua a alguém.',
        acaoLabel: 'Assumir',
        badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        accent: 'border-l-gray-400',
    },
};

/** Ordem de exibição — a mesma dos filtros e dos KPIs. */
export const ORDEM_FILAS: EsteiraFila[] = [
    'regua_d4', 'regua_d10', 'regua_d17', 'notificacao_extrajudicial', 'regua_d24',
    'mora_sem_assinatura', 'mora_sem_envio', 'mora_prazo_vencido',
    'advertencia_sem_assinatura', 'advertencia_sem_envio', 'advertencia_prazo_vencido',
    'distrato_aguardando_vigor', 'distrato_aguardando_cliente',
    'distrato_assinado_sem_envio', 'distrato_em_aberto',
    'negativacao_pendente', 'negativacao_retirar', 'negativado_spc',
    'followup_vencido', 'sem_retorno', 'sem_contato', 'sem_responsavel',
];

/** Departamentos que podem cuidar da esteira. Mesma lista da régua. */
export { DEPARTAMENTOS_COBRANCA } from '../LembretesCobranca/etapas.ts';

/**
 * ── ETAPAS DO FLUXO ──────────────────────────────────────────────────────────
 *
 * As 16 filas são fatiadas por TIPO de documento (mora × advertência × distrato)
 * e estado. Isso serve para filtrar, mas esconde o fluxo: "aguardando
 * assinatura" aparece em quatro filas diferentes, e ninguém enxerga que o funil
 * inteiro está empoçado no mesmo ponto.
 *
 * A etapa é o corte TRANSVERSAL — a mesma fase do processo, seja qual for o
 * documento. É o que o modo Etapas mostra e o filtro de fila não consegue dar.
 *
 * Ordem = ordem real do processo, da esquerda para a direita.
 */
export type EtapaFluxo =
    | 'contato'
    | 'emitir'
    | 'assinatura'
    | 'envio'
    | 'conferir'
    | 'negociacao';

export interface EtapaMeta {
    etapa: EtapaFluxo;
    titulo: string;
    /** A pergunta que a coluna responde — vira o subtítulo. */
    pergunta: string;
    accent: string;
    chip: string;
}

export const ETAPAS_FLUXO: EtapaMeta[] = [
    {
        etapa: 'contato',
        titulo: 'Falar com o cliente',
        pergunta: 'Quem ainda não foi contatado?',
        accent: 'border-t-amber-400',
        chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    },
    {
        etapa: 'emitir',
        titulo: 'Emitir documento',
        pergunta: 'Para quem falta gerar a notificação?',
        accent: 'border-t-orange-500',
        chip: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    },
    {
        etapa: 'assinatura',
        titulo: 'Aguardando assinatura',
        pergunta: 'Quem está segurando a assinatura?',
        accent: 'border-t-violet-500',
        chip: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
    },
    {
        etapa: 'envio',
        titulo: 'Assinado, falta enviar',
        pergunta: 'O que já pode ir para o cliente?',
        accent: 'border-t-teal-500',
        chip: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
    },
    {
        etapa: 'conferir',
        titulo: 'Conferir e decidir',
        pergunta: 'Pagou? Negativa ou encerra?',
        accent: 'border-t-red-500',
        chip: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    },
    {
        etapa: 'negociacao',
        titulo: 'Negociação aberta',
        pergunta: 'Quem precisa de retorno ou de dono?',
        accent: 'border-t-purple-500',
        chip: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    },
];

/**
 * Em que etapa cada fila cai.
 *
 * ⚠️ D+17 fica em "emitir" e não em "contato": a etapa dele É gerar a
 * notificação extrajudicial. D+24 fica em "conferir" porque o trabalho é
 * checar o pagamento antes de negativar — o contato já aconteceu.
 */
export const ETAPA_DA_FILA: Record<EsteiraFila, EtapaFluxo> = {
    regua_d4: 'contato',
    regua_d10: 'contato',
    regua_d17: 'emitir',
    notificacao_extrajudicial: 'emitir',
    regua_d24: 'conferir',

    mora_sem_assinatura: 'assinatura',
    advertencia_sem_assinatura: 'assinatura',
    distrato_aguardando_vigor: 'assinatura',
    distrato_aguardando_cliente: 'assinatura',
    distrato_em_aberto: 'assinatura',

    mora_sem_envio: 'envio',
    advertencia_sem_envio: 'envio',
    distrato_assinado_sem_envio: 'envio',

    mora_prazo_vencido: 'conferir',
    advertencia_prazo_vencido: 'conferir',
    negativacao_pendente: 'conferir',
    negativacao_retirar: 'conferir',
    // Só acompanhamento: o quadro não mostra (ver QuadroEtapas).
    negativado_spc: 'negociacao',

    followup_vencido: 'negociacao',
    sem_retorno: 'negociacao',
    sem_contato: 'contato',
    sem_responsavel: 'negociacao',
};
